import { useState } from "react";
import { api } from "@/lib/api";
import { ShieldCheck, XCircle } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";

const human = (s) => (s || "").replace(/_/g, " ");

export default function ReportValidate() {
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  usePageMeta("Validate a verification report — EuropEdu",
    "Check whether a EuropEdu document verification report is genuine and see exactly what was and was not checked.");

  const check = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/verification/reports/validate/${code.trim()}`);
      setResult(data);
    } catch (e) { setResult({ valid: false, message: "That code could not be checked." }); }
    setBusy(false);
  };

  return (
    <div className="max-w-3xl mx-auto px-5 py-10" data-testid="report-validate-page">
      <p className="overline">Report validation</p>
      <h1 className="mt-3 text-3xl font-semibold">Validate a verification report</h1>
      <p className="mt-3 text-sm text-slate-600">
        Enter the validation code printed on a EuropEdu verification report. A genuine report always
        states which checks were performed and which were explicitly not performed.
      </p>
      <div className="mt-6 flex flex-wrap gap-3">
        <input data-testid="validation-code-input" className="field max-w-xs" placeholder="e.g. 4F2A9C1B7D"
          value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
        <button data-testid="validate-report-button" onClick={check} disabled={!code.trim() || busy} className="btn btn-primary">
          Validate report
        </button>
      </div>

      {result && (
        <section className="card p-6 mt-8" data-testid="validation-result">
          <p className={`flex items-center gap-2 font-semibold ${result.valid ? "text-emerald-700" : "text-rose-700"}`}>
            {result.valid ? <ShieldCheck className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
            {result.valid ? `Genuine report ${result.reference}` : "No matching report"}
          </p>
          <p className="mt-2 text-sm text-slate-700">{result.message}</p>
          {result.valid && (
            <dl className="mt-4 text-sm space-y-2">
              <div><dt className="text-slate-500">Service</dt><dd className="font-medium">{result.scope_name}</dd></div>
              <div><dt className="text-slate-500">Classification</dt><dd className="font-medium" data-testid="validation-classification">{human(result.classification)}</dd></div>
              <div><dt className="text-slate-500">Issued</dt><dd>{new Date(result.issued_at).toLocaleString("en-GB")}</dd></div>
              <div><dt className="text-slate-500">Checks performed</dt><dd><ul className="list-disc ml-4">{(result.checks_performed || []).map((c, i) => <li key={i}>{c}</li>)}</ul></dd></div>
              <div><dt className="text-slate-500">Checks explicitly not performed</dt><dd><ul className="list-disc ml-4">{(result.checks_not_performed || []).map((c, i) => <li key={i}>{c}</li>)}</ul></dd></div>
            </dl>
          )}
          {result.valid && <p className="mt-4 text-xs text-slate-500">{result.limitations}</p>}
        </section>
      )}
    </div>
  );
}
